import { InputNumber, Table } from 'antd';
import Column from 'antd/es/table/Column';
import ColumnGroup from 'antd/es/table/ColumnGroup';
import React from 'react';
import { formatVND, layTenNhomNhanSu } from '../../helpers/common';
import { ChiTietLuongHoanThanhNhiemVuItem } from '../../models/luong/LuongHoanThanhNhiemVuModel';

const romanNumerals = ["I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

export interface FlatRowChiTietLuongHoanThanhNhiemVu {
    key: string;
    type: 'grand-summary' | 'group-summary' | 'employee';
    groupIndex?: string;
    employeeIndex?: number;
    originalIndex: number;
    id?: number;
    idLuongHoanThanhNhiemVu?: number;
    idNhanVien?: number;
    idChucVu?: number;
    idDonVi?: number;
    idNhom?: number;
    tenChucVu?: string;
    tenDonVi?: string;
    tenNhom?: string;
    ma?: string;
    hoTen: string | React.ReactNode;
    ketQuaHTNV?: string;
    heSoHTNV?: number;
    ghiChu?: string;
    soTienDuocNhan?: number;
    isQuanLy?: boolean;
    [key: string]: any;
}

export const extractMonths = (items: ChiTietLuongHoanThanhNhiemVuItem[], thangLayDuLieu?: string): string[] => {
    const monthSet = new Set<string>();

    if (thangLayDuLieu) {
        thangLayDuLieu.split(',').forEach((m) => {
            const trimmed = m.trim();
            if (trimmed) {
                const parts = trimmed.split('/');
                if (parts.length === 2) {
                    const formatted = `${parts[0].padStart(2, '0')}/${parts[1]}`;
                    monthSet.add(formatted);
                } else {
                    monthSet.add(trimmed);
                }
            }
        });
    }

    if (items && items.length > 0) {
        items.forEach((item) => {
            Object.keys(item).forEach((k) => {
                if (
                    k.startsWith('heSoHTNV_') ||
                    k.startsWith('ketQuaHTNV_') ||
                    k.startsWith('heSo_') ||
                    k.startsWith('ketQua_')
                ) {
                    const parts = k.split('_');
                    if (parts.length >= 3) {
                        const month = parts[1].padStart(2, '0');
                        const year = parts[2];
                        monthSet.add(`${month}/${year}`);
                    }
                }
            });
        });
    }

    return Array.from(monthSet).sort((a, b) => {
        const [ma, ya] = a.split('/').map(Number);
        const [mb, yb] = b.split('/').map(Number);
        if (ya !== yb) return (ya || 0) - (yb || 0);
        return (ma || 0) - (mb || 0);
    });
};

export const getMonthlyValue = (record: any, prefix: string, month: string) => {
    if (!record) return undefined;
    const parts = month.split('/');
    const mPadded = parts[0].padStart(2, '0');
    const mRaw = parseInt(parts[0], 10).toString();
    const y = parts[1] || '';

    const prefixes = prefix === 'heSoHTNV' ? ['heSoHTNV', 'heSo'] : [prefix, prefix.replace('HTNV', '')];
    for (const p of prefixes) {
        const keyPadded = `${p}_${mPadded}_${y}`;
        if (record[keyPadded] !== undefined && record[keyPadded] !== null && record[keyPadded] !== '') {
            return record[keyPadded];
        }
        const keyRaw = `${p}_${mRaw}_${y}`;
        if (record[keyRaw] !== undefined && record[keyRaw] !== null && record[keyRaw] !== '') {
            return record[keyRaw];
        }
        const keyDirect = `${p}_${month.replace('/', '_')}`;
        if (record[keyDirect] !== undefined && record[keyDirect] !== null && record[keyDirect] !== '') {
            return record[keyDirect];
        }
    }
    return undefined;
};

const buildFlatRows = (
    items: ChiTietLuongHoanThanhNhiemVuItem[],
    loai?: number,
    thangLayDuLieu?: string
): FlatRowChiTietLuongHoanThanhNhiemVu[] => {
    const flatRows: FlatRowChiTietLuongHoanThanhNhiemVu[] = [];
    if (!items || items.length === 0) return flatRows;

    const months = extractMonths(items, thangLayDuLieu);

    // Group items by idNhom (fallback: item.isQuanLy ? 3 : 1)
    const groups: { [key: number]: { originalIndex: number; item: ChiTietLuongHoanThanhNhiemVuItem }[] } = {};
    items.forEach((item, index) => {
        const nhomId = item.idNhom || (item.isQuanLy ? 3 : 1);
        if (!groups[nhomId]) {
            groups[nhomId] = [];
        }
        groups[nhomId].push({ originalIndex: index, item });
    });

    const preferredOrder = [3, 7, 1];
    const allGroupIds = Object.keys(groups).map(Number);
    const sortedGroupIds: number[] = [
        ...preferredOrder.filter(id => allGroupIds.includes(id)),
        ...allGroupIds.filter(id => !preferredOrder.includes(id))
    ];

    // Compute Grand Totals (Chỉ tính từ items của nhân viên, không tính dòng nhóm)
    let grandHeSo = 0;
    let grandSoTien = 0;
    const grandMonthlyHeSo: { [key: string]: number } = {};
    months.forEach(m => {
        grandMonthlyHeSo[`heSoHTNV_${m.replace('/', '_')}`] = 0;
    });

    items.forEach(item => {
        grandHeSo += item.heSoHTNV || 0;
        grandSoTien += item.soTienDuocNhan || 0;

        months.forEach(m => {
            const mKey = m.replace('/', '_');
            const val = getMonthlyValue(item, 'heSoHTNV', m);
            if (val && !isNaN(Number(val))) {
                grandMonthlyHeSo[`heSoHTNV_${mKey}`] += Number(val);
            }
        });
    });

    // 1. Grand Summary Row at top
    const grandRow: FlatRowChiTietLuongHoanThanhNhiemVu = {
        key: 'grand-summary',
        type: 'grand-summary',
        originalIndex: -1,
        hoTen: 'Tổng cộng',
        heSoHTNV: Math.round(grandHeSo * 100) / 100,
        soTienDuocNhan: grandSoTien,
    };
    months.forEach(m => {
        const mKey = m.replace('/', '_');
        grandRow[`heSoHTNV_${mKey}`] = Math.round((grandMonthlyHeSo[`heSoHTNV_${mKey}`] || 0) * 100) / 100;
    });
    flatRows.push(grandRow);

    let employeeSTT = 1;

    sortedGroupIds.forEach((nhomId, groupIdx) => {
        const groupItems = groups[nhomId];
        const groupName = nhomId === 7 ? "Trưởng Ban kiểm soát" : layTenNhomNhanSu(nhomId);
        const groupRoman = romanNumerals[groupIdx] || (groupIdx + 1).toString();

        let groupHeSo = 0;
        let groupSoTien = 0;
        const groupMonthlyHeSo: { [key: string]: number } = {};
        months.forEach(m => {
            groupMonthlyHeSo[`heSoHTNV_${m.replace('/', '_')}`] = 0;
        });

        groupItems.forEach(({ item }) => {
            groupHeSo += item.heSoHTNV || 0;
            groupSoTien += item.soTienDuocNhan || 0;

            months.forEach(m => {
                const mKey = m.replace('/', '_');
                const val = getMonthlyValue(item, 'heSoHTNV', m);
                if (val && !isNaN(Number(val))) {
                    groupMonthlyHeSo[`heSoHTNV_${mKey}`] += Number(val);
                }
            });
        });

        // 2. Group Summary Row
        const groupRow: FlatRowChiTietLuongHoanThanhNhiemVu = {
            key: `group-summary-${nhomId}`,
            type: 'group-summary',
            originalIndex: -1,
            groupIndex: groupRoman,
            hoTen: groupName,
            heSoHTNV: Math.round(groupHeSo * 100) / 100,
            soTienDuocNhan: groupSoTien,
        };
        months.forEach(m => {
            const mKey = m.replace('/', '_');
            groupRow[`heSoHTNV_${mKey}`] = Math.round((groupMonthlyHeSo[`heSoHTNV_${mKey}`] || 0) * 100) / 100;
        });
        flatRows.push(groupRow);

        // 3. Employee Rows
        groupItems.forEach(({ originalIndex, item }) => {
            flatRows.push({
                ...item,
                key: `employee-${item.idNhanVien || item.id}-${originalIndex}`,
                type: 'employee',
                employeeIndex: employeeSTT++,
                originalIndex,
            });
        });
    });

    return flatRows;
};

interface TableChiTietLuongHoanThanhNhiemVuProps {
    loading: boolean;
    dataSource: ChiTietLuongHoanThanhNhiemVuItem[];
    isEdit?: boolean;
    onChangeItem?: (index: number, field: string, value: any) => void;
    loai?: number;
    thangLayDuLieu?: string;
}

const TableChiTietLuongHoanThanhNhiemVu = ({
    loading,
    dataSource,
    isEdit,
    onChangeItem,
    loai,
    thangLayDuLieu
}: TableChiTietLuongHoanThanhNhiemVuProps) => {
    const months = extractMonths(dataSource, thangLayDuLieu);
    const flatRows = buildFlatRows(dataSource, loai, thangLayDuLieu);

    const scrollX = Math.max(
        1200,
        60 + 120 + 220 + 180 + 200 + (months.length === 0 ? 200 : months.length * 190 + 120) + 160 + 200
    );

    return (
        <>
            <style>{`
                .table-row-grand-summary td {
                    background-color: #fff9e6 !important;
                    font-weight: bold;
                }
                .table-row-group-summary td {
                    background-color: #e6f7ff !important;
                    font-weight: bold;
                }
            `}</style>
            <Table
                sticky
                pagination={false}
                loading={loading}
                bordered
                scroll={{ x: scrollX }}
                locale={{ emptyText: 'Không có dữ liệu' }}
                dataSource={flatRows}
                rowClassName={(record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                    if (record.type === 'grand-summary') return 'table-row-grand-summary';
                    if (record.type === 'group-summary') return 'table-row-group-summary';
                    return '';
                }}
            >
                <Column
                    title="STT"
                    width={60}
                    fixed="left"
                    align="center"
                    render={(_v, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary') return "";
                        if (record.type === 'group-summary') return <strong>{record.groupIndex}</strong>;
                        return record.employeeIndex;
                    }}
                />
                <Column
                    title="Mã NV"
                    dataIndex="ma"
                    width={120}
                    fixed="left"
                    render={(v, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    title="Họ và tên"
                    dataIndex="hoTen"
                    width={220}
                    fixed="left"
                    render={(v, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{v}</strong>;
                        }
                        return v;
                    }}
                />
                <Column
                    title="Chức vụ"
                    dataIndex="tenChucVu"
                    width={180}
                    render={(v, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                <Column
                    title="Phòng ban"
                    dataIndex="tenDonVi"
                    width={200}
                    render={(v, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return v;
                    }}
                />
                
                {months.length === 0 ? (
                    <>
                        <Column
                            title="Kết quả HTNV"
                            dataIndex="ketQuaHTNV"
                            width={100}
                            align="center"
                            render={(val, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                                if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                                if (!val || val === '--' || val === '-') return '';
                                return val;
                            }}
                        />
                        <Column
                            title="Hệ số HTNV"
                            dataIndex="heSoHTNV"
                            width={100}
                            align="right"
                            render={(val, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                                if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                    return <strong>{val != null && val !== 0 ? Number(val).toFixed(2) : ''}</strong>;
                                }
                                if (!val || val === 0 || val === '0' || val === '--' || val === '-') return '';
                                return typeof val === 'number' ? val.toFixed(2) : val;
                            }}
                        />
                    </>
                ) : (
                    <>
                        {months.map((month) => {
                            const mKey = month.replace('/', '_');
                            return (
                                <ColumnGroup title={`Tháng ${month}`} key={month} align="center">
                                    <Column
                                        title="Kết quả HTNV"
                                        align="center"
                                        width={95}
                                        render={(_, record: any) => {
                                            if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                                            const val = getMonthlyValue(record, 'ketQuaHTNV', month);
                                            if (!val || val === '--' || val === '-') return '';
                                            return val;
                                        }}
                                    />
                                    <Column
                                        title="Hệ số HTNV"
                                        align="right"
                                        width={95}
                                        render={(_, record: any) => {
                                            if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                                const val = record[`heSoHTNV_${mKey}`];
                                                return <strong>{val != null && val !== 0 ? Number(val).toFixed(2) : ''}</strong>;
                                            }
                                            const val = getMonthlyValue(record, 'heSoHTNV', month);
                                            if (!val || val === 0 || val === '0' || val === '--' || val === '-') return '';
                                            return typeof val === 'number' ? val.toFixed(2) : val;
                                        }}
                                    />
                                </ColumnGroup>
                            );
                        })}
                        <Column
                            title="Tổng hệ số HTNV"
                            dataIndex="heSoHTNV"
                            width={120}
                            align="right"
                            render={(val, record: any) => {
                                if (record.type === 'grand-summary' || record.type === 'group-summary') {
                                    return <strong>{val != null && val !== 0 ? Number(val).toFixed(2) : ''}</strong>;
                                }
                                if (!val || val === 0 || val === '0') return '';
                                return typeof val === 'number' ? val.toFixed(2) : val;
                            }}
                        />
                    </>
                )}

                <Column
                    title="Lương HTNV"
                    dataIndex="soTienDuocNhan"
                    width={160}
                    align="right"
                    render={(value, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') {
                            return <strong>{formatVND(value)}</strong>;
                        }
                        if (isEdit) {
                            return (
                                <InputNumber
                                    min={0}
                                    step={1000}
                                    value={value}
                                    style={{ width: '100%' }}
                                    formatter={(val) => `${val}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                                    parser={(val) => Number(`${val}`.replace(/,/g, '')) || 0}
                                    onChange={(val) => onChangeItem && onChangeItem(record.originalIndex, 'soTienDuocNhan', Number(val) || 0)}
                                />
                            );
                        }
                        return formatVND(value);
                    }}
                />
                <Column
                    title="Ghi chú"
                    dataIndex="ghiChu"
                    align="center"
                    width={200}
                    render={(val, record: FlatRowChiTietLuongHoanThanhNhiemVu) => {
                        if (record.type === 'grand-summary' || record.type === 'group-summary') return "";
                        return val;
                    }}
                />
            </Table>
        </>
    );
};

export default TableChiTietLuongHoanThanhNhiemVu;